/** @jsxImportSource @emotion/react */

import { Icon } from '@app/components/Atoms/Icon'
import { DialogLayersPreviewProps } from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog/LayersPreview'
import { LayerDetailsReport } from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDetailsFetcher'
import { LayerDetailsModal } from '@app/components/Organisms/Modals/LayerDetailsModal'
import { riskMapInstanceAtom } from '@app/risk-migration/atoms/riskMap.atoms'
import { useAtomValue } from 'jotai'
import {
  handleSetLayerVisibility,
  handleToggleLayerInteractivity,
} from '@app/contexts/ModuleMapContexts/utils/layers'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'
import { riskLayersAtom } from '@app/risk-migration/atoms/riskLayers.atoms'
import { useRiskLayersState } from '@app/risk-migration/hooks/useRiskLayersState'
import type { MapLayer } from '@app/types/layerTypes'
import { Box, IconButton, Modal, useTheme } from '@mui/material'
import { useSetAtom } from 'jotai'
import { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { buttonsParent } from './LayerControlButtons.styles'
const initialInformationDetailsModal = {
  isOpen: false,
  content: null,
}

interface InformationDetailsModal {
  isOpen: boolean
  content: JSX.Element | null
}

export const LayerControlButtons = ({
  dialogProps,
  layer,
  layerDetails,
  isDialogPreview,
  handleDeleteLayer,
}: {
  dialogProps?: DialogLayersPreviewProps
  isDialogPreview: boolean
  layerDetails?: LayerDetailsReport[] | null
  layer: MapLayer
  handleDeleteLayer: (props: { selectedLayer: MapLayer }) => void
}) => {
  const theme = useTheme()
  const {
    toggleLiveFeedVisibility,
    toggleLiveFeedInteractivity,
    activeLiveLayers,
    setActiveLiveLayers,
  } = useLiveContext()
  const map = useAtomValue(riskMapInstanceAtom)
  const setRiskLayers = useSetAtom(riskLayersAtom)
  const { layers: storeLayers } = useRiskLayersState()
  const [informationDetailsModal, setInformationDetailsModal] = useState<InformationDetailsModal>(
    initialInformationDetailsModal,
  )

  const onToggleLayerVisibility = useCallback(
    (layer: MapLayer) => {
      if (isDialogPreview) {
        if (!dialogProps || !dialogProps.handleToggleLayerVisibility) return
        dialogProps.handleToggleLayerVisibility(layer)
      } else {
        if (!map) return
        if (layer.layerType === 'live') {
          toggleLiveFeedVisibility(layer.id)
        } else {
          handleSetLayerVisibility({
            hideLayers: layer.visible ? [layer] : [],
            showLayers: layer.visible ? [] : [layer],
            allLayers: storeLayers,
            map,
            setLayers: setRiskLayers,
          })
        }
      }
    },
    [dialogProps, isDialogPreview, map, setRiskLayers, storeLayers, toggleLiveFeedVisibility],
  )

  const onToggleLayerInteractivity = useCallback(
    (layer: MapLayer) => {
      if (!map) return

      if (layer.layerType === 'live') {
        toggleLiveFeedInteractivity(layer.id)
      } else {
        handleToggleLayerInteractivity({
          layer,
          layers: storeLayers,
          setLayers: setRiskLayers,
        })
      }
    },
    [map, toggleLiveFeedInteractivity, storeLayers, setRiskLayers],
  )

  const isLayerVisible = useMemo(() => {
    if (layer.layerType === 'live') {
      return activeLiveLayers.find((activeLayer) => activeLayer.id === layer.id)?.visible
    } else {
      return layer.visible
    }
  }, [activeLiveLayers, layer])

  const isLayerInteractive = useMemo(() => {
    if (layer.layerType === 'live') {
      return !activeLiveLayers.find((activeLayer) => activeLayer.id === layer.id)
        ?.interactivityDisabled
    } else {
      return !layer.interactivityDisabled
    }
  }, [activeLiveLayers, layer])

  return (
    <Box css={buttonsParent({ theme })}>
      {!isDialogPreview && (
        <>
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              onToggleLayerVisibility(layer)
            }}
          >
            <Icon
              iconName={isLayerVisible ? 'EyeVisible' : 'EyeHidden'}
              colour={theme.palette.primary.main}
              size="medium"
            />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              onToggleLayerInteractivity(layer)
            }}
          >
            <Icon
              iconName={isLayerInteractive ? 'SpeakerNotes' : 'SpeakerNotesOff'}
              colour={theme.palette.primary.main}
              size="medium"
            />
          </IconButton>
        </>
      )}
      {isDialogPreview && layerDetails && layerDetails.length > 0 && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation()
            setInformationDetailsModal({
              isOpen: true,
              content: (
                <LayerDetailsModal
                  isOpen={true}
                  layer={layer}
                  layerDetails={layerDetails}
                  handleClose={() => setInformationDetailsModal(initialInformationDetailsModal)}
                />
              ),
            })
          }}
        >
          <Icon iconName="Info" colour={theme.palette.primary.main} size="medium" />
        </IconButton>
      )}
      <IconButton
        onClick={(e) => {
          e.stopPropagation()
          handleDeleteLayer({
            selectedLayer: layer,
          })

          if (layer.layerType === 'live') {
            setActiveLiveLayers(
              activeLiveLayers.filter((activeLayer) => activeLayer.id !== layer.id),
            )
          }
        }}
      >
        <Icon iconName="Delete" colour={theme.palette.primary.main} size="medium" />
      </IconButton>
      {informationDetailsModal.isOpen &&
        createPortal(<Modal open>{informationDetailsModal.content ?? <></>}</Modal>, document.body)}
    </Box>
  )
}
